import { t } from "@web-monorepo/i18n/macro";
import { getImageSrc } from "@web-monorepo/static-assets";
import { throttle } from "es-toolkit";
import { useEffect, useMemo, useRef } from "react";
import { appMetadataNoise } from "src/pods/noise";
import errorMessages, { ErrorType } from "src/pods/noise/data/errorMessages";
import AppModal from "src/shared/components/AppModal";
import AppPage from "src/shared/components/AppPage";
import logEvent from "src/utils/logEvent";
import { SensitivitySlider } from "./SensitivitySlider";
import styles from "./NoiseRemote.module.css";

const bargraph = getImageSrc("toolkit/src/pods/noise/assets/bargraph.png");

type NoiseRemoteProps = {
  classId: string;
  mountPoint: string;
  displayOpen: (path: string, payload?: { type: string; payload: { gain: 0; showSensitivityInput: boolean } }) => void;
  displayClose: VoidFunction;
  remoteClose: VoidFunction;
  sendNoise: (payload: { gain: number }) => void;
  isCasting: boolean;
  isConnected: boolean;
  microphonePermissionErrorName: ErrorType;
};

export default function NoiseRemote(props: NoiseRemoteProps) {
  const {
    classId,
    mountPoint,
    displayOpen,
    displayClose,
    remoteClose,
    sendNoise,
    isCasting,
    isConnected,
    microphonePermissionErrorName,
  } = props;

  // Stable throttled sender (500ms) — recreated only if sendNoise identity
  // changes.
  const changeSensitivity = useMemo(() => throttle((gain: number) => sendNoise({ gain }), 500), [sendNoise]);

  // Setup on mount: open the display + log open/close.
  // Note: the latest-displayOpen behavior is preserved with a ref so this
  // effect can stay mount-only.
  const setupRef = useRef({ displayOpen, mountPoint });
  setupRef.current = { displayOpen, mountPoint };
  useEffect(() => {
    setupRef.current.displayOpen("/apps/display/noise", {
      type: "RECEIVE_NOISE",
      payload: { gain: 0, showSensitivityInput: setupRef.current.mountPoint === "teach" },
    });
    logEvent({ eventName: "noise.setup.open" });
    return () => {
      logEvent({ eventName: "noise.setup.close" });
    };
  }, []);

  if (mountPoint === "teach") return <div />;

  let errorMessage;
  if (!isCasting) {
    if (isConnected) {
      errorMessage = t`Noise Meter works with your computer's microphone. Please start DojoCast above to use.`;
    } else {
      errorMessage = t`Noise Meter works with your computer's microphone. Please log into your class from a computer and try again.`;
    }
  } else if (microphonePermissionErrorName) {
    errorMessage = errorMessages(microphonePermissionErrorName);
  }

  return (
    <AppPage {...appMetadataNoise()} classId={classId} mountPoint={mountPoint}>
      <AppModal
        {...appMetadataNoise()}
        onRequestHide={() => {
          displayClose();
          remoteClose();
        }}
        onRequestStart={() => displayOpen("/apps/display/noise")}
        onRequestStop={() => displayClose()}
      >
        <section className={styles.display}>
          <img src={bargraph} className={styles.bargraph} alt="" />
          {isCasting && !microphonePermissionErrorName && (
            <div className={styles.sliderWrap}>
              <SensitivitySlider
                aria-label={t`Noise sensitivity range`}
                label={t`Sensitivity`}
                defaultValue={0}
                min={-125}
                max={125}
                step={1}
                onChange={changeSensitivity}
              />
            </div>
          )}
          {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
        </section>
      </AppModal>
    </AppPage>
  );
}
